import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  getAdvisorDiagnostics, resendInvitation, deleteDiagnostic,
  getAdvisorStats, getMultipleDiagnosticsRevenue,
  DiagnosticWithClient, AdvisorStatsResult, DiagnosticRevenueEntry,
} from '../../services/adminService';
import { getNotifications } from '../../services/notificationService';
import { inviteClientToDiagnostic } from '../../services/invitationService';
import { supabase } from '../../lib/supabase';
import { dpsTheme } from '../../styles/dpsTheme';
import { BarChart3, UserPlus, FileText, RefreshCw, Clock, CheckCircle2, Search, Trash2, TrendingUp } from 'lucide-react';
import { useDeviceSize } from '../../hooks/useMediaQuery';

// ── Helpers ────────────────────────────────────────────────────────────────────

async function getLatestSnapshot(diagnosticId: string) {
  const { data } = await supabase
    .from('score_snapshots')
    .select('score')
    .eq('diagnostic_id', diagnosticId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  return data?.score ?? null;
}

type ClientStatus = 'not_invited' | 'invited_pending' | 'can_reinvite' | 'active';

function getClientStatus(diag: DiagnosticWithClient): ClientStatus {
  if (!diag.client_id) return 'not_invited';
  if (!diag.client_last_sign_in) {
    const daysSinceInvite = diag.invitation_sent_at
      ? (Date.now() - new Date(diag.invitation_sent_at).getTime()) / (1000 * 60 * 60 * 24)
      : 999;
    return daysSinceInvite >= 7 ? 'can_reinvite' : 'invited_pending';
  }
  return 'active';
}

function getScoreColor(score: number): string {
  if (score >= 75) return '#10b981';
  if (score >= 50) return '#f59e0b';
  if (score >= 25) return '#f97316';
  return '#ef4444';
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatEuros(n: number): string {
  if (n === 0) return '0 €';
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €';
}

// ── Carte diagnostic compacte ──────────────────────────────────────────────────

interface AdvisorDiagnosticCardProps {
  diag: DiagnosticWithClient & { current_score?: number; actions_completed?: number; revenue?: number };
  onReport: () => void;
  onInvite: () => void;
  onReinvite: () => void;
  onDelete: () => void;
  hasUnreadNotification?: boolean;
}

function AdvisorDiagnosticCard({ diag, onReport, onInvite, onReinvite, onDelete, hasUnreadNotification = false }: AdvisorDiagnosticCardProps) {
  const score            = diag.current_score ?? diag.security_score;
  const actionsCompleted = diag.actions_completed ?? 0;
  const revenue          = diag.revenue ?? 0;
  const scoreColor = getScoreColor(score);
  const status = getClientStatus(diag);
  const city = diag.diagnostic_data?.city ?? '—';

  const statusBadge = () => {
    switch (status) {
      case 'not_invited':
        return (
          <button
            onClick={onInvite}
            title="Inviter un client"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '5px',
              padding: '5px 10px', borderRadius: '6px', fontSize: '12px',
              fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
              border: '1px solid rgba(139,92,246,0.35)',
              background: 'rgba(139,92,246,0.1)', color: '#a78bfa',
            }}
          >
            <UserPlus size={12} />
            Inviter
          </button>
        );
      case 'invited_pending':
        return (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            padding: '5px 10px', borderRadius: '6px', fontSize: '12px',
            fontWeight: 500, whiteSpace: 'nowrap',
            background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)',
            color: '#f59e0b',
          }}>
            <Clock size={12} />
            En attente
          </span>
        );
      case 'can_reinvite':
        return (
          <button
            onClick={onReinvite}
            title={`Relancer l'invitation (${diag.client_email})`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '5px',
              padding: '5px 10px', borderRadius: '6px', fontSize: '12px',
              fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
              border: '1px solid rgba(249,115,22,0.35)',
              background: 'rgba(249,115,22,0.1)', color: '#f97316',
            }}
          >
            <RefreshCw size={12} />
            Relancer
          </button>
        );
      case 'active':
        return (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            padding: '5px 10px', borderRadius: '6px', fontSize: '12px',
            fontWeight: 500, whiteSpace: 'nowrap',
            background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)',
            color: '#10b981',
          }}>
            <CheckCircle2 size={12} />
            Client actif
          </span>
        );
    }
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '12px',
      padding: '12px 16px',
      background: dpsTheme.colors.background.darker,
      border: `1px solid ${hasUnreadNotification ? 'rgba(251,146,60,0.4)' : dpsTheme.colors.border.secondary}`,
      borderRadius: dpsTheme.borderRadius.md,
      transition: 'border-color 0.15s',
    }}
      onMouseEnter={e => (e.currentTarget.style.borderColor = hasUnreadNotification ? 'rgba(251,146,60,0.6)' : dpsTheme.colors.border.primary)}
      onMouseLeave={e => (e.currentTarget.style.borderColor = hasUnreadNotification ? 'rgba(251,146,60,0.4)' : dpsTheme.colors.border.secondary)}
    >
      {/* Colonne gauche : commune + date + référence */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {/* Badge notification non lue */}
          {hasUnreadNotification && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '3px',
              padding: '2px 7px', borderRadius: '8px', fontSize: '11px', fontWeight: 700,
              background: 'rgba(251,146,60,0.15)', border: '1px solid rgba(251,146,60,0.35)',
              color: '#fb923c', flexShrink: 0,
            }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              Mise à jour
            </span>
          )}
          <span style={{
            fontSize: '14px', fontWeight: 600,
            color: dpsTheme.colors.text.primary,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {city}
          </span>
          {diag.client_reference && (
            <span style={{
              fontSize: '11px', padding: '2px 7px', borderRadius: '10px',
              background: 'rgba(148,163,184,0.12)',
              border: '1px solid rgba(148,163,184,0.2)',
              color: dpsTheme.colors.text.muted,
              fontFamily: 'monospace',
              flexShrink: 0,
            }}>
              {diag.client_reference}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
          <span style={{ fontSize: '12px', color: dpsTheme.colors.text.muted }}>
            {formatDate(diag.created_at)}
          </span>
          <span style={{ color: dpsTheme.colors.border.secondary, fontSize: '10px' }}>·</span>
          {actionsCompleted > 0 ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px', color: '#10b981' }}>
              <TrendingUp size={10} />
              {actionsCompleted} action{actionsCompleted > 1 ? 's' : ''} · {formatEuros(revenue)}
            </span>
          ) : (
            <span style={{ fontSize: '11px', color: dpsTheme.colors.text.muted }}>Aucune action réalisée</span>
          )}
        </div>
      </div>

      {/* Colonne centre : score */}
      <div style={{
        flexShrink: 0,
        width: '44px', height: '44px',
        borderRadius: '50%',
        background: `${scoreColor}1a`,
        border: `2px solid ${scoreColor}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{ fontSize: '13px', fontWeight: 700, color: scoreColor }}>
          {score}
        </span>
      </div>

      {/* Colonne droite : rapport + statut client + corbeille */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
        <button
          onClick={onReport}
          title="Voir le rapport"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            padding: '5px 10px', borderRadius: '6px', fontSize: '12px',
            fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
            border: 'none',
            background: dpsTheme.colors.accent.blue.gradient,
            color: 'white',
          }}
        >
          <FileText size={12} />
          Rapport
        </button>

        {statusBadge()}

        <button
          onClick={onDelete}
          title="Supprimer ce diagnostic"
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '28px', height: '28px', borderRadius: '6px', flexShrink: 0,
            border: '1px solid transparent',
            background: 'transparent',
            color: dpsTheme.colors.text.muted,
            cursor: 'pointer',
            transition: 'color 0.15s, background 0.15s, border-color 0.15s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.color = '#f87171';
            e.currentTarget.style.background = 'rgba(239,68,68,0.1)';
            e.currentTarget.style.borderColor = 'rgba(239,68,68,0.3)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.color = dpsTheme.colors.text.muted;
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = 'transparent';
          }}
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

// ── Dashboard principal ────────────────────────────────────────────────────────

type DiagnosticEnriched = DiagnosticWithClient & {
  current_score?: number;
  actions_completed?: number;
  revenue?: number;
};

export function AdvisorDashboard() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const { isMobile } = useDeviceSize();

  const [diagnostics,         setDiagnostics]         = useState<DiagnosticEnriched[]>([]);
  const [advisorStats,        setAdvisorStats]        = useState<AdvisorStatsResult | null>(null);
  const [loading,             setLoading]             = useState(true);
  const [unreadDiagnosticIds, setUnreadDiagnosticIds] = useState<Set<string>>(new Set());

  // ── Filtres ──────────────────────────────────────────────────────────────────
  const [filterCity, setFilterCity] = useState('');
  const [filterRef, setFilterRef] = useState('');

  // ── Modal invitation ──────────────────────────────────────────────────────────
  const [invitingDiag, setInvitingDiag] = useState<DiagnosticEnriched | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [isInviting, setIsInviting] = useState(false);

  // ── Modal réinvitation ────────────────────────────────────────────────────────
  const [reinvitingDiag, setReinvitingDiag] = useState<DiagnosticEnriched | null>(null);
  const [isReinviting, setIsReinviting] = useState(false);

  // ── Modal suppression ─────────────────────────────────────────────────────────
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // ── Chargement ────────────────────────────────────────────────────────────────

  const loadDiagnostics = async (advisorId: string): Promise<DiagnosticEnriched[]> => {
    const raw = await getAdvisorDiagnostics(advisorId);
    if (raw.length === 0) return [];
    const ids = raw.map(d => d.id);
    // Snapshots (N+1 existant) + revenue batch en parallèle
    const [snapshots, revenueRows] = await Promise.all([
      Promise.all(raw.map(d => getLatestSnapshot(d.id))),
      getMultipleDiagnosticsRevenue(ids),
    ]);
    const revMap = new Map<string, DiagnosticRevenueEntry>(revenueRows.map(r => [r.diagnostic_id, r]));
    return raw.map((diag, i) => ({
      ...diag,
      current_score:      snapshots[i] ?? diag.security_score,
      actions_completed:  revMap.get(diag.id)?.actions_completed ?? 0,
      revenue:            revMap.get(diag.id)?.revenue           ?? 0,
    }));
  };

  useEffect(() => {
    let mounted = true;
    if (!currentUser) return;

    (async () => {
      try {
        setLoading(true);
        const [enriched, stats, notifs] = await Promise.all([
          loadDiagnostics(currentUser.id),
          getAdvisorStats(currentUser.id),
          getNotifications(50).catch(() => [] as Awaited<ReturnType<typeof getNotifications>>),
        ]);
        if (mounted) {
          setDiagnostics(enriched);
          setAdvisorStats(stats);
          setUnreadDiagnosticIds(new Set(
            notifs
              .filter(n => !n.read && n.data.diagnostic_id)
              .map(n => n.data.diagnostic_id!)
          ));
        }
      } catch {
        toast.error('Erreur lors du chargement des diagnostics');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [currentUser?.id]);

  // ── Filtrage local ────────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    let result = diagnostics;
    if (filterCity.trim()) {
      const q = filterCity.trim().toLowerCase();
      result = result.filter(d =>
        (d.diagnostic_data?.city ?? '').toLowerCase().includes(q)
      );
    }
    if (filterRef.trim()) {
      const q = filterRef.trim().toLowerCase();
      result = result.filter(d =>
        (d.client_reference ?? '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [diagnostics, filterCity, filterRef]);

  const isFiltered = filterCity.trim() !== '' || filterRef.trim() !== '';

  // ── Actions ───────────────────────────────────────────────────────────────────

  const handleInviteClient = async () => {
    if (!inviteEmail.trim() || !invitingDiag) return;
    setIsInviting(true);
    try {
      const result = await inviteClientToDiagnostic(inviteEmail.trim(), invitingDiag.id);
      if (result.success) {
        // Marquer invitation_sent_at localement
        await supabase
          .from('diagnostics')
          .update({ invitation_sent_at: new Date().toISOString() })
          .eq('id', invitingDiag.id);

        toast.success(`Invitation envoyée à ${inviteEmail.trim()}`);
        setInvitingDiag(null);
        setInviteEmail('');
        if (currentUser) setDiagnostics(await loadDiagnostics(currentUser.id));
      } else {
        toast.error(result.error || "Échec de l'invitation");
      }
    } catch {
      toast.error("Erreur lors de l'invitation");
    } finally {
      setIsInviting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteDiagnostic(deleteTarget);
      setDiagnostics(prev => prev.filter(d => d.id !== deleteTarget));
      setDeleteTarget(null);
      toast.success('Diagnostic supprimé');
    } catch {
      toast.error('Erreur lors de la suppression');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleResendInvitation = async () => {
    if (!reinvitingDiag || !reinvitingDiag.client_email || !userProfile) return;
    setIsReinviting(true);
    try {
      const result = await resendInvitation(
        reinvitingDiag.id,
        reinvitingDiag.client_email,
        currentUser!.id,
        userProfile.company_id ?? ''
      );
      if (result.success) {
        toast.success(`Invitation relancée à ${reinvitingDiag.client_email}`);
        setReinvitingDiag(null);
        if (currentUser) setDiagnostics(await loadDiagnostics(currentUser.id));
      } else {
        toast.error(result.error || 'Échec de la réinvitation');
      }
    } catch {
      toast.error('Erreur lors de la réinvitation');
    } finally {
      setIsReinviting(false);
    }
  };

  // ── Stats ─────────────────────────────────────────────────────────────────────

  const paidCount = diagnostics.filter(d => d.report_paid).length;
  const activeClients = diagnostics.filter(d => getClientStatus(d) === 'active').length;
  const latestScore = diagnostics.length > 0
    ? (diagnostics[0].current_score ?? diagnostics[0].security_score)
    : null;

  // ── Render ────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: dpsTheme.colors.background.primary, paddingTop: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: dpsTheme.colors.text.muted }}>Chargement...</div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: dpsTheme.typography.fontFamily, minHeight: '100vh', background: dpsTheme.colors.background.primary, paddingTop: '64px', color: dpsTheme.colors.text.secondary }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: isMobile ? `${dpsTheme.spacing.lg} ${dpsTheme.spacing.md}` : dpsTheme.spacing['3xl'] }}>

        {/* ── Header ── */}
        <div style={{ marginBottom: dpsTheme.spacing['2xl'] }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '4px 12px', borderRadius: '20px', background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)', marginBottom: '12px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            <span style={{ fontSize: dpsTheme.typography.fontSize.xs, fontWeight: dpsTheme.typography.fontWeight.semibold, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Conseiller</span>
          </div>
          <div>
            <h1 style={{ fontSize: isMobile ? dpsTheme.typography.fontSize['2xl'] : dpsTheme.typography.fontSize['4xl'], fontWeight: dpsTheme.typography.fontWeight.bold, color: dpsTheme.colors.text.primary, marginBottom: '8px' }}>
              Mes diagnostics
            </h1>
            <p style={{ color: dpsTheme.colors.text.muted }}>
              {userProfile?.company_name} · {userProfile?.first_name} {userProfile?.last_name}
            </p>
          </div>
        </div>

        {/* ── Stats ── */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)', gap: dpsTheme.spacing.lg, marginBottom: dpsTheme.spacing['2xl'] }}>
          {[
            { label: 'Total diagnostics',   value: diagnostics.length,                                        icon: <BarChart3 size={20} strokeWidth={2} />,         accent: '#a78bfa', accentBg: 'rgba(139,92,246,0.15)',  accentBorder: 'rgba(139,92,246,0.3)' },
            { label: 'Score moyen',          value: latestScore ? `${latestScore}/100` : '—',                  icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>, accent: '#a78bfa', accentBg: 'rgba(139,92,246,0.15)', accentBorder: 'rgba(139,92,246,0.3)' },
            { label: 'Clients actifs',       value: activeClients,                                             icon: <CheckCircle2 size={20} strokeWidth={2} />,      accent: '#a78bfa', accentBg: 'rgba(139,92,246,0.15)',  accentBorder: 'rgba(139,92,246,0.3)' },
            { label: 'Rapports premium',     value: paidCount,                                                 icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>, accent: '#a78bfa', accentBg: 'rgba(139,92,246,0.15)', accentBorder: 'rgba(139,92,246,0.3)' },
            { label: 'Actions réalisées',    value: advisorStats?.total_actions_completed ?? 0,                icon: <TrendingUp size={20} strokeWidth={2} />,        accent: '#10b981', accentBg: 'rgba(16,185,129,0.15)',   accentBorder: 'rgba(16,185,129,0.3)' },
            { label: 'CA généré',            value: formatEuros(advisorStats?.total_revenue ?? 0),             icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M14.31 8a4 4 0 0 0-4.62 0C8.56 8.72 8 9.8 8 11c0 1.77 1.12 3.29 2.72 3.82L12 15l1.28.82C14.88 16.35 16 17.87 16 19.66"/><path d="M9.69 16a4 4 0 0 0 4.62 0"/></svg>, accent: '#10b981', accentBg: 'rgba(16,185,129,0.15)', accentBorder: 'rgba(16,185,129,0.3)' },
          ].map(stat => (
            <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              style={{ background: dpsTheme.colors.background.card, backdropFilter: 'blur(20px)', border: `1px solid ${dpsTheme.colors.border.primary}`, borderRadius: dpsTheme.borderRadius.lg, padding: dpsTheme.spacing.xl }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ padding: '10px', borderRadius: dpsTheme.borderRadius.md, background: stat.accentBg, border: `1px solid ${stat.accentBorder}`, color: stat.accent }}>{stat.icon}</div>
                <div>
                  <p style={{ fontSize: dpsTheme.typography.fontSize.xs, color: dpsTheme.colors.text.muted, marginBottom: '2px' }}>{stat.label}</p>
                  <p style={{ fontSize: dpsTheme.typography.fontSize['2xl'], fontWeight: dpsTheme.typography.fontWeight.bold, color: dpsTheme.colors.text.primary }}>{stat.value}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* ── Liste diagnostics ── */}
        <div style={{ background: dpsTheme.colors.background.card, backdropFilter: 'blur(20px)', border: `1px solid ${dpsTheme.colors.border.primary}`, borderRadius: dpsTheme.borderRadius.lg, padding: dpsTheme.spacing['2xl'] }}>

          {/* Header liste */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: dpsTheme.spacing.xl, flexWrap: 'wrap', gap: '12px' }}>
            <h2 style={{ fontSize: dpsTheme.typography.fontSize.xl, fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.text.primary }}>
              Diagnostics ({diagnostics.length})
            </h2>
            <button
              onClick={() => navigate('/diagnostic')}
              style={{ padding: '10px 20px', borderRadius: '8px', fontWeight: 500, fontSize: '14px', cursor: 'pointer', border: 'none', background: dpsTheme.colors.accent.blue.gradient, color: 'white', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
              Nouveau diagnostic
            </button>
          </div>

          {/* ── Barre de recherche ── */}
          {diagnostics.length > 0 && (
            <div style={{ marginBottom: dpsTheme.spacing.lg }}>
              <div style={{ display: 'flex', gap: '12px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: dpsTheme.colors.text.muted, pointerEvents: 'none' }} />
                  <input
                    type="text"
                    placeholder="Rechercher par commune…"
                    value={filterCity}
                    onChange={e => setFilterCity(e.target.value)}
                    style={{
                      width: '100%', padding: '8px 12px 8px 32px',
                      borderRadius: '8px', border: `1px solid ${dpsTheme.colors.border.primary}`,
                      background: dpsTheme.colors.background.darker,
                      color: dpsTheme.colors.text.primary, fontSize: '13px',
                      boxSizing: 'border-box', outline: 'none',
                    }}
                  />
                </div>
                <div style={{ position: 'relative', flex: 1 }}>
                  <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: dpsTheme.colors.text.muted, pointerEvents: 'none' }} />
                  <input
                    type="text"
                    placeholder="Référence client…"
                    value={filterRef}
                    onChange={e => setFilterRef(e.target.value)}
                    style={{
                      width: '100%', padding: '8px 12px 8px 32px',
                      borderRadius: '8px', border: `1px solid ${dpsTheme.colors.border.primary}`,
                      background: dpsTheme.colors.background.darker,
                      color: dpsTheme.colors.text.primary, fontSize: '13px',
                      boxSizing: 'border-box', outline: 'none',
                    }}
                  />
                </div>
              </div>
              {isFiltered && (
                <p style={{ fontSize: '12px', color: dpsTheme.colors.text.muted, marginTop: '8px' }}>
                  {filtered.length} diagnostic{filtered.length > 1 ? 's' : ''} trouvé{filtered.length > 1 ? 's' : ''}
                  {' '}<button onClick={() => { setFilterCity(''); setFilterRef(''); }} style={{ background: 'none', border: 'none', color: '#a78bfa', cursor: 'pointer', fontSize: '12px', padding: 0, textDecoration: 'underline' }}>Effacer les filtres</button>
                </p>
              )}
            </div>
          )}

          {/* ── Contenu ── */}
          {diagnostics.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px', background: dpsTheme.colors.background.darker, border: `1px solid ${dpsTheme.colors.border.secondary}`, borderRadius: dpsTheme.borderRadius.lg }}>
              <BarChart3 size={48} strokeWidth={1.5} style={{ color: dpsTheme.colors.text.muted, margin: '0 auto 16px' }} />
              <p style={{ color: dpsTheme.colors.text.muted, marginBottom: '24px' }}>Aucun diagnostic enregistré</p>
              <button onClick={() => navigate('/diagnostic')} style={{ padding: '10px 20px', borderRadius: '8px', fontWeight: 500, fontSize: '14px', cursor: 'pointer', border: 'none', background: dpsTheme.colors.accent.blue.gradient, color: 'white' }}>
                Créer le premier diagnostic
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px', background: dpsTheme.colors.background.darker, border: `1px solid ${dpsTheme.colors.border.secondary}`, borderRadius: dpsTheme.borderRadius.lg }}>
              <p style={{ color: dpsTheme.colors.text.muted }}>Aucun résultat pour ces filtres.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {filtered.map(diag => (
                <AdvisorDiagnosticCard
                  key={diag.id}
                  diag={diag}
                  onReport={() => {
                    setUnreadDiagnosticIds(prev => { const s = new Set(prev); s.delete(diag.id); return s; });
                    navigate(`/dps/report/${diag.id}`);
                  }}
                  onInvite={() => { setInvitingDiag(diag); setInviteEmail(''); }}
                  onReinvite={() => setReinvitingDiag(diag)}
                  onDelete={() => setDeleteTarget(diag.id)}
                  hasUnreadNotification={unreadDiagnosticIds.has(diag.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Modal invitation ── */}
      {invitingDiag && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            style={{ background: dpsTheme.colors.background.card, border: `1px solid ${dpsTheme.colors.border.primary}`, borderRadius: dpsTheme.borderRadius.xl, padding: '32px', maxWidth: '440px', width: '100%' }}>
            <h3 style={{ fontSize: dpsTheme.typography.fontSize.xl, fontWeight: dpsTheme.typography.fontWeight.bold, color: dpsTheme.colors.text.primary, marginBottom: '8px' }}>
              Inviter un client
            </h3>
            <p style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.muted, marginBottom: '24px' }}>
              Un email d'invitation sera envoyé. Le client aura accès uniquement au diagnostic <strong style={{ color: dpsTheme.colors.text.secondary }}>{invitingDiag.diagnostic_data?.city ?? ''}</strong>.
            </p>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: dpsTheme.colors.text.secondary, marginBottom: '6px' }}>Email *</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleInviteClient()}
                placeholder="client@exemple.fr"
                autoFocus
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${dpsTheme.colors.border.primary}`, background: dpsTheme.colors.background.darker, color: dpsTheme.colors.text.primary, fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setInvitingDiag(null); setInviteEmail(''); }}
                style={{ padding: '10px 20px', borderRadius: '8px', border: `1px solid ${dpsTheme.colors.border.primary}`, background: 'transparent', color: dpsTheme.colors.text.secondary, fontSize: '14px', cursor: 'pointer' }}
              >
                Annuler
              </button>
              <button
                onClick={handleInviteClient}
                disabled={isInviting || !inviteEmail.trim()}
                style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: dpsTheme.colors.accent.blue.gradient, color: 'white', fontSize: '14px', fontWeight: 500, cursor: isInviting || !inviteEmail.trim() ? 'not-allowed' : 'pointer', opacity: isInviting || !inviteEmail.trim() ? 0.6 : 1 }}
              >
                {isInviting ? 'Envoi…' : "Envoyer l'invitation"}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* ── Modal suppression ── */}
      {deleteTarget && (() => {
        const diag = diagnostics.find(d => d.id === deleteTarget);
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              style={{ background: dpsTheme.colors.background.card, border: `1px solid rgba(239,68,68,0.3)`, borderRadius: dpsTheme.borderRadius.xl, padding: '32px', maxWidth: '440px', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Trash2 size={18} color="#f87171" />
                </div>
                <h3 style={{ fontSize: dpsTheme.typography.fontSize.xl, fontWeight: dpsTheme.typography.fontWeight.bold, color: dpsTheme.colors.text.primary }}>
                  Supprimer ce diagnostic ?
                </h3>
              </div>
              <p style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.muted, marginBottom: '24px', lineHeight: '1.6' }}>
                Cette action est irréversible. Le diagnostic de{' '}
                <strong style={{ color: dpsTheme.colors.text.secondary }}>{diag?.diagnostic_data?.city ?? ''}</strong>{' '}
                du{' '}
                <strong style={{ color: dpsTheme.colors.text.secondary }}>{diag ? formatDate(diag.created_at) : ''}</strong>{' '}
                sera définitivement supprimé, ainsi que toutes les actions et l'historique associés.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setDeleteTarget(null)}
                  disabled={isDeleting}
                  style={{ padding: '10px 20px', borderRadius: '8px', border: `1px solid ${dpsTheme.colors.border.primary}`, background: 'transparent', color: dpsTheme.colors.text.secondary, fontSize: '14px', cursor: 'pointer' }}
                >
                  Annuler
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: 'white', fontSize: '14px', fontWeight: 600, cursor: isDeleting ? 'not-allowed' : 'pointer', opacity: isDeleting ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <Trash2 size={14} />
                  {isDeleting ? 'Suppression...' : 'Supprimer'}
                </button>
              </div>
            </motion.div>
          </div>
        );
      })()}

      {/* ── Modal réinvitation ── */}
      {reinvitingDiag && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            style={{ background: dpsTheme.colors.background.card, border: `1px solid ${dpsTheme.colors.border.primary}`, borderRadius: dpsTheme.borderRadius.xl, padding: '32px', maxWidth: '440px', width: '100%' }}>
            <h3 style={{ fontSize: dpsTheme.typography.fontSize.xl, fontWeight: dpsTheme.typography.fontWeight.bold, color: dpsTheme.colors.text.primary, marginBottom: '8px' }}>
              Relancer l'invitation
            </h3>
            <p style={{ fontSize: dpsTheme.typography.fontSize.sm, color: dpsTheme.colors.text.muted, marginBottom: '24px' }}>
              Renvoyer l'invitation à{' '}
              <strong style={{ color: dpsTheme.colors.text.secondary }}>{reinvitingDiag.client_email}</strong> ?
            </p>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setReinvitingDiag(null)}
                style={{ padding: '10px 20px', borderRadius: '8px', border: `1px solid ${dpsTheme.colors.border.primary}`, background: 'transparent', color: dpsTheme.colors.text.secondary, fontSize: '14px', cursor: 'pointer' }}
              >
                Annuler
              </button>
              <button
                onClick={handleResendInvitation}
                disabled={isReinviting}
                style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #f97316, #ea580c)', color: 'white', fontSize: '14px', fontWeight: 500, cursor: isReinviting ? 'not-allowed' : 'pointer', opacity: isReinviting ? 0.6 : 1 }}
              >
                {isReinviting ? 'Envoi…' : 'Confirmer la relance'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
